import axios from "axios";
import { clearAllCookies, getCookie, redirectToLogin } from "./Utils";

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

// Token management
const TOKEN_KEY = 'auth_token';
const getStoredToken = () => sessionStorage.getItem(TOKEN_KEY);
const removeStoredToken = () => sessionStorage.removeItem(TOKEN_KEY);
const storeToken = (token) => sessionStorage.setItem(TOKEN_KEY, token);

export const axiosRequest = axios.create({
  baseURL: process.env.REACT_APP_BaseUrl,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json"
  },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

const setAuthToken = (token) => {
  if (token) {
    storeToken(token);
    // Set the auth header - adjust format based on your API requirements
    axiosRequest.defaults.headers.common['Authorization'] = token;
  } else {
    removeStoredToken();
    delete axiosRequest.defaults.headers.common['Authorization'];
  }
};

// Function to extract token from response based on your API structure
const extractTokenFromResponse = (response) => {
  // Adjust these checks based on your actual API response structure
  if (response.data?.token) return response.data.token;
  if (response.data?.access_token) return response.data.access_token;
  if (response.headers?.['x-auth-token']) return response.headers['x-auth-token'];
  return null;
};

const refreshSession = async () => {
  try {
    const response = await axiosRequest.post("/session/refresh");
    const newToken = extractTokenFromResponse(response);

    if (newToken) {
      setAuthToken(newToken);
      return true;
    }

    console.error("No token received in refresh response", response);
    return false;
  } catch (error) {
    console.error("Failed to refresh session:", error?.response?.data || error.message);
    return false;
  }
};

// Add retry logic for network errors
const retryRequest = async (error, retryCount = 0) => {
  const { config } = error;

  if (!config || retryCount >= MAX_RETRIES) {
    return Promise.reject(error);
  }

  // Wait before retrying with exponential backoff
  const delay = RETRY_DELAY * Math.pow(2, retryCount);
  await new Promise(resolve => setTimeout(resolve, delay));

  // Create a new promise for the retry
  const retryConfig = {
    ...config,
    _retryCount: retryCount + 1
  };

  // Ensure we have the latest token
  const currentToken = getStoredToken();
  if (currentToken) {
    retryConfig.headers['Authorization'] = currentToken;
  }

  return axiosRequest(retryConfig);
};

// Initialize auth header from session storage
const initializeAuth = () => {
  const token = getStoredToken();
  if (token) {
    setAuthToken(token);
  }
};

// Initialize auth on load
initializeAuth();

axiosRequest.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle network errors with retry logic
    if (error.message === 'Network Error' && (!originalRequest._retryCount || originalRequest._retryCount < MAX_RETRIES)) {
      const retryCount = (originalRequest._retryCount || 0) + 1;
      console.log(`Retrying request (${retryCount}/${MAX_RETRIES})`);
      return retryRequest(error, originalRequest._retryCount || 0);
    }

    // Handle 401 errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => axiosRequest(originalRequest))
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshed = await refreshSession();
        if (refreshed) {
          // Update the authorization header of the failed request
          const token = getStoredToken();
          if (token) {
            originalRequest.headers['Authorization'] = token;
          }

          processQueue();
          return axiosRequest(originalRequest);
        } else {
          processQueue(error);
          if (getCookie("firstName")) {
            setAuthToken(null); // Clear invalid token
            redirectToLogin();
          }
          return Promise.reject(new Error('Session refresh failed'));
        }
      } catch (refreshError) {
        processQueue(refreshError);
        if (getCookie("firstName")) {
          setAuthToken(null); // Clear invalid token
          redirectToLogin();
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);
